import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'

/* ----------------------------------------------------------------------------
   blue2purple editorial kit: offset headers with a quote-bar lede, geometric
   halftone marks, right-aligned benefit cells with hairline dividers, and the
   two-row scrolling tile wall. See docs/design/BRAND-STYLE.md.
---------------------------------------------------------------------------- */

export type GeoVariant = 'chevrons' | 'ring' | 'hex' | 'stripes' | 'bars' | 'cube'

const GEO_COLORS: Record<string, string> = {
  pink: '#ff00f2',
  purple: 'var(--color-landing-secondary)',
  blue: 'var(--color-landing-accent)',
  green: '#00d722',
}

/**
 * Small geometric mark with a halftone-dot fill, like the reference's section
 * glyphs. Decorative only.
 */
export function GeoMark({
  variant,
  color = 'pink',
  className,
}: {
  variant: GeoVariant
  color?: keyof typeof GEO_COLORS
  className?: string
}) {
  const fill = GEO_COLORS[color] ?? GEO_COLORS.pink
  const pid = `geo-dots-${variant}-${color}`
  return (
    <svg viewBox="0 0 64 64" className={cn('h-14 w-14', className)} aria-hidden>
      <defs>
        <pattern id={pid} width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="4" fill={fill} />
          <circle cx="2" cy="2" r="0.7" fill="rgba(255,255,255,0.55)" />
        </pattern>
      </defs>
      {variant === 'chevrons' && (
        <>
          <path d="M8 12 L30 32 L8 52 Z" fill={`url(#${pid})`} />
          <path d="M34 12 L56 32 L34 52 Z" fill={`url(#${pid})`} />
        </>
      )}
      {variant === 'ring' && (
        <>
          <rect x="8" y="8" width="48" height="48" fill={`url(#${pid})`} />
          <circle cx="32" cy="32" r="14" fill="none" stroke="white" strokeWidth="7" />
        </>
      )}
      {variant === 'hex' && (
        <path d="M32 4 L58 18 L58 46 L32 60 L6 46 L6 18 Z" fill={`url(#${pid})`} />
      )}
      {variant === 'stripes' && (
        <>
          <clipPath id={`${pid}-clip`}>
            <rect x="8" y="8" width="48" height="48" />
          </clipPath>
          <g clipPath={`url(#${pid}-clip)`}>
            <rect x="8" y="8" width="48" height="48" fill="white" />
            {[-40, -24, -8, 8, 24, 40].map((o) => (
              <rect key={o} x={o} y="-10" width="9" height="110" transform="rotate(45 32 32)" fill={`url(#${pid})`} />
            ))}
          </g>
        </>
      )}
      {variant === 'bars' && (
        <>
          <rect x="14" y="8" width="15" height="48" fill={`url(#${pid})`} />
          <rect x="35" y="8" width="15" height="48" fill={`url(#${pid})`} />
        </>
      )}
      {variant === 'cube' && (
        <path d="M32 2 L56 14 L56 44 L32 62 L8 44 L8 14 Z" fill={`url(#${pid})`} />
      )}
    </svg>
  )
}

/** Black boxy call-to-action, uppercase with arrow (reference's GET IN TOUCH). */
export function EditorialCta({ label, href, className }: { label: string; href: string; className?: string }) {
  return (
    <Link
      to={href}
      className={cn('landing-cta landing-cta--solid landing-cta--md landing-btn-sweep', className)}
    >
      {label}
      <ArrowRight className="h-4 w-4" />
    </Link>
  )
}

/**
 * Section opener in the reference's editorial layout: a huge uppercase
 * heading (pass .landing-highlight spans in `heading`), a grey lede behind a
 * hairline quote bar, and the boxy CTA. `layout="row"` spreads the three
 * across one line (integrations header); `layout="stack"` stacks heading,
 * then lede + CTA (benefits header).
 */
export function EditorialHeader({
  heading,
  lede,
  cta,
  layout = 'stack',
  className,
}: {
  heading: React.ReactNode
  lede?: string
  cta?: { label: string; href: string }
  layout?: 'stack' | 'row'
  className?: string
}) {
  const ledeBlock = lede && (
    <ScrollReveal direction="none" delay={0.15}>
      <p className="landing-text-body-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-300)] border-l border-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-700)] pl-5 max-w-xl">
        {lede}
      </p>
    </ScrollReveal>
  )
  const ctaBlock = cta && (
    <ScrollReveal direction="none" delay={0.25}>
      <EditorialCta label={cta.label} href={cta.href} />
    </ScrollReveal>
  )

  if (layout === 'row') {
    return (
      <div className={cn('grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] lg:items-center', className)}>
        <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
          {heading}
        </TextReveal>
        {ledeBlock}
        {ctaBlock}
      </div>
    )
  }

  return (
    <div className={cn('max-w-4xl', className)}>
      <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white mb-10">
        {heading}
      </TextReveal>
      <div className="space-y-10">
        {ledeBlock}
        {ctaBlock}
      </div>
    </div>
  )
}

export interface EditorialCellData {
  title: string
  description: string
  geo: GeoVariant
  color?: keyof typeof GEO_COLORS
  /** Small grey index above the mark, e.g. "01". */
  index?: string
}

/**
 * The reference's benefit grid: right-aligned cells, geometric mark on top,
 * uppercase display title, grey copy, hairline dividers between columns.
 */
export function EditorialGrid({
  cells,
  columns = 3,
  align = 'right',
  titleSize = 'sm',
  className,
}: {
  cells: EditorialCellData[]
  columns?: 1 | 2 | 3 | 4
  /** Cell title size: 'sm' (reference's small caps) or 'lg' (twice that). */
  titleSize?: 'sm' | 'lg'
  /** Cell text alignment: 'right' (reference's benefit grid) or 'left' (case-grid side). */
  align?: 'right' | 'left'
  className?: string
}) {
  const colClass =
    columns === 1
      ? 'grid-cols-1'
      : columns === 2
        ? 'sm:grid-cols-2'
        : columns === 3
          ? 'md:grid-cols-3'
          : 'md:grid-cols-2 lg:grid-cols-4'
  return (
    <StaggerContainer
      className={cn(
        'grid gap-y-16 gap-x-10',
        columns > 2 && 'md:divide-x md:divide-[var(--color-landing-gray-200)] dark:md:divide-[var(--color-landing-gray-800)]',
        colClass,
        className
      )}
    >
      {cells.map((cell) => (
        <StaggerItem
          key={cell.title}
          className={cn(
            'flex flex-col gap-5',
            align === 'right' ? 'items-end text-right' : 'items-start text-left',
            columns > 2 && 'md:px-8 first:md:pl-0 last:md:pr-0'
          )}
        >
          {cell.index && (
            <span className={cn('landing-text-overline text-[var(--color-landing-gray-400)] -mb-3', align === 'right' ? 'self-end' : 'self-start')}>
              {cell.index}
            </span>
          )}
          <GeoMark variant={cell.geo} color={cell.color} />
          <h3
            className={cn(
              'font-[family-name:var(--font-landing-display)] uppercase leading-[1.5] tracking-normal text-[var(--color-landing-gray-900)] dark:text-white text-balance',
              titleSize === 'lg' ? 'text-[1.25rem] md:text-2xl leading-[1.4]' : 'text-[0.625rem] md:text-xs'
            )}
          >
            {cell.title}
          </h3>
          <p className="landing-text-body-sm text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-300)]">
            {cell.description}
          </p>
        </StaggerItem>
      ))}
    </StaggerContainer>
  )
}

/**
 * Two-column editorial split (reference's cases/benefits sections): the
 * header column is sticky below the nav while the content column scrolls
 * past it. `side` places the header left or right; the right variant
 * right-aligns its text, like "From ambition to measurable impact."
 */
export function StickySplit({
  side = 'left',
  headerWidth = 'half',
  header,
  children,
  className,
}: {
  side?: 'left' | 'right'
  /** 'narrow' gives the header two fifths and the content three fifths. */
  headerWidth?: 'half' | 'narrow'
  header: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  const cols =
    headerWidth === 'half'
      ? 'lg:grid-cols-2'
      : side === 'left'
        ? 'lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]'
        : 'lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'
  return (
    <div className={cn('grid gap-16 lg:gap-20 items-start', cols, className)}>
      {side === 'left' ? (
        <>
          <div className="lg:sticky lg:top-28 self-start">{header}</div>
          <div>{children}</div>
        </>
      ) : (
        <>
          <div className="order-2 lg:order-1">{children}</div>
          <div className="order-1 lg:order-2 lg:sticky lg:top-28 self-start lg:text-right flex flex-col lg:items-end">
            {header}
          </div>
        </>
      )}
    </div>
  )
}

export interface MarqueeTile {
  name: string
  node?: React.ReactNode
}

function TileRow({ tiles, reverse }: { tiles: MarqueeTile[]; reverse?: boolean }) {
  const row = (keyPrefix: string) => (
    <div className="flex shrink-0 gap-4 pr-4">
      {tiles.map((tile, i) => (
        <div
          key={`${keyPrefix}-${i}`}
          className="flex h-36 w-44 md:h-44 md:w-56 shrink-0 items-center justify-center border border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)] bg-white dark:bg-[#12121a] px-6"
        >
          {tile.node ?? (
            <span className="text-center text-base font-semibold text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-300)]">
              {tile.name}
            </span>
          )}
        </div>
      ))}
    </div>
  )
  return (
    <div
      className={cn(
        'flex w-max hover:[animation-play-state:paused]',
        reverse ? 'animate-marquee-reverse' : 'animate-marquee'
      )}
    >
      {row('a')}
      {row('b')}
    </div>
  )
}

/**
 * Two counter-scrolling rows of bordered square tiles (the reference's
 * "Platform certified" wall). Constant linear motion, pauses on hover,
 * static under prefers-reduced-motion (global rule kills the marquee).
 */
export function TileMarquee({ rowA, rowB, className }: { rowA: MarqueeTile[]; rowB: MarqueeTile[]; className?: string }) {
  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-white dark:from-[#0a0a0f] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-white dark:from-[#0a0a0f] to-transparent" />
      <div className="space-y-4">
        <TileRow tiles={rowA} />
        <TileRow tiles={rowB} reverse />
      </div>
    </div>
  )
}
